/**
 * Reminders domain — store integration layer.
 *
 * Extracted from storeIntegration.ts to reduce file size and isolate
 * the reminders concern. Zero coupling to other store integrations.
 */

import { logger } from '@/utils/logger';

import { useAppStore } from './useAppStore';

import type { AgendaEvent } from './useAppStore';

/**
 * When a closed reminder closed, as near as the API says: its completion
 * time, else (a pass, which records none) the event date, else when it was due.
 */
const closedAt = (r: AgendaEvent) => r.completed_at ?? r.event_date ?? r.surface_at ?? r.created_at;

/** Done's order: most recently closed first. */
const newestClosedFirst = (rows: AgendaEvent[]) =>
  [...rows].sort((a, b) => Date.parse(closedAt(b)) - Date.parse(closedAt(a)));

/**
 * Enhanced reminders service that integrates with store
 */
export const remindersService = {
  refreshReminders: async () => {
    const store = useAppStore.getState();

    try {
      store.setIsLoadingReminders(true);
      store.setRemindersError(null);

      // Dynamic import to avoid circular dependencies
      const { remindersApi, activeListCapped, ACTIVE_REMINDERS_LIMIT } =
        await import('@features/agenda/services/remindersApi');
      const [active, completed, cancelled] = await Promise.all([
        remindersApi.getReminders(undefined, ACTIVE_REMINDERS_LIMIT),
        // Closed lists come newest-first from the server, so the limit drops old history.
        remindersApi.getReminders('completed', ACTIVE_REMINDERS_LIMIT),
        // Passed events are cancelled; Done shows them with their reason.
        remindersApi.getReminders('cancelled', ACTIVE_REMINDERS_LIMIT),
      ]);

      store.setReminders(active);
      store.setRemindersCapped(activeListCapped(active));
      store.setCompletedReminders(newestClosedFirst([...completed, ...cancelled]));
    } catch (error) {
      logger.error('Error loading reminders:', error);
      store.setRemindersError(error instanceof Error ? error.message : 'Failed to load events');
    } finally {
      store.setIsLoadingReminders(false);
    }
  },

  createReminder: async (data: {
    content: string;
    note?: string | null | undefined;
    // Minute-precision contract.
    when?: string | undefined;
    remind_before?: string | null | undefined;
    items?: string[] | undefined;
    recurrence_days?: number | null | undefined;
    recurrence_rule?:
      { type: 'weekdays' | 'weekly'; days: number[] } | { type: 'yearly' } | null | undefined;
  }) => {
    try {
      const { remindersApi } = await import('@features/agenda/services/remindersApi');
      const reminder = await remindersApi.createReminder(data);
      await remindersService.refreshReminders();
      return reminder;
    } catch (error) {
      // The form that called this shows the failure; the list-load error must not claim it.
      logger.error('Error creating reminder:', error);
      throw error;
    }
  },

  updateReminder: async (
    id: number,
    data: {
      content?: string | undefined;
      note?: string | null | undefined;
      // Minute-precision contract.
      when?: string | undefined;
      remind_before?: string | null | undefined;
      recurrence_days?: number | null | undefined;
      recurrence_rule?:
        { type: 'weekdays' | 'weekly'; days: number[] } | { type: 'yearly' } | null | undefined;
    }
  ) => {
    try {
      const { remindersApi } = await import('@features/agenda/services/remindersApi');
      const reminder = await remindersApi.updateReminder(id, data);
      await remindersService.refreshReminders();
      return reminder;
    } catch (error) {
      // The form that called this shows the failure; the list-load error must not claim it.
      logger.error('Error updating reminder:', error);
      throw error;
    }
  },

  deleteReminder: async (id: number) => {
    const store = useAppStore.getState();

    try {
      const { remindersApi } = await import('@features/agenda/services/remindersApi');
      await remindersApi.deleteReminder(id);
      store.removeReminder(id);
    } catch (error) {
      // The form that called this shows the failure; the list-load error must not claim it.
      logger.error('Error deleting reminder:', error);
      throw error;
    }
  },

  completeReminder: async (id: number) => {
    const store = useAppStore.getState();

    try {
      const { remindersApi } = await import('@features/agenda/services/remindersApi');
      await remindersApi.completeReminder(id);
      await remindersService.refreshReminders();
    } catch (error) {
      logger.error('Error completing reminder:', error);
      store.setRemindersError(
        error instanceof Error ? error.message : 'Failed to complete event'
      );
      throw error;
    }
  },

  /**
   * Reopen a completed reminder and move it back to the active list.
   *
   * Deliberately does NOT call refreshReminders() or set global error/loading
   * state: both would toggle isLoadingReminders/remindersError, which unmounts
   * the open edit modal that called this handler. Instead the reminder is
   * optimistically moved in memory and any API failure is rethrown so the
   * modal can catch it and revert its checkbox.
   */
  reopenReminder: async (id: number) => {
    const { remindersApi } = await import('@features/agenda/services/remindersApi');
    // Rethrow on failure (see JSDoc) so the modal can revert its checkbox.
    await remindersApi.reopenReminder(id);

    // Optimistically move the reminder from the completed list to the active
    // list with status reverted to pending. Read state AFTER the await so a
    // concurrent list update during the round-trip isn't clobbered.
    const store = useAppStore.getState();
    const moved = store.completedReminders.find(r => r.id === id);
    if (moved) {
      store.setCompletedReminders(store.completedReminders.filter(r => r.id !== id));
      store.setReminders([
        ...store.reminders,
        { ...moved, status: 'pending' as const, completed_at: null },
      ]);
    }
  },

  addItem: async (reminderId: number, item: string) => {
    const store = useAppStore.getState();

    try {
      const { remindersApi } = await import('@features/agenda/services/remindersApi');
      await remindersApi.addItem(reminderId, item);
      await remindersService.refreshReminders();
    } catch (error) {
      logger.error('Error adding item:', error);
      store.setRemindersError(error instanceof Error ? error.message : 'Failed to add item');
      throw error;
    }
  },

  removeItem: async (reminderId: number, item: string) => {
    const store = useAppStore.getState();

    try {
      const { remindersApi } = await import('@features/agenda/services/remindersApi');
      await remindersApi.removeItem(reminderId, item);
      await remindersService.refreshReminders();
    } catch (error) {
      logger.error('Error removing item:', error);
      store.setRemindersError(error instanceof Error ? error.message : 'Failed to remove item');
      throw error;
    }
  },
};
